import { createPopper, Instance, Placement } from "@popperjs/core";
import Control, { Options } from "ol/control/Control";

export interface IControlBaseOptions extends Options {
    symbol?: SVGElement,
    collapsible?: boolean,
    collapsedSymbol?: SVGElement,
    clickHandler?: () => any,
    tooltipText?: string,
    tooltipLocation?: Placement,
    tooltipContainer?: HTMLDivElement
}

interface ITooltipResult {
    popper:Instance,
    tooltipTextDiv :HTMLDivElement
}

export default class ControlBase extends Control {
    protected container: HTMLDivElement;
    protected collapsibleContent: HTMLDivElement;
    protected tooltipTextDiv: HTMLDivElement;
    protected button: HTMLButtonElement;
    protected popper: Instance;
    protected tooltipDiv: HTMLDivElement;
    protected boundToggleCollapsedState;

    constructor(options: IControlBaseOptions) {
        let container = document.createElement("div");
        container.classList.add("ol-control");
        let button = document.createElement("button");
        button.type = "button";
        if (options.symbol) {
            options.symbol.classList.add("symbol");
            button.appendChild(options.symbol);
        }
        if (options.clickHandler) {
            button.addEventListener("click", options.clickHandler);
        }
        container.appendChild(button);
        options.element = container;

        super(options);

        this.container = container;
        this.button = button;
        this.collapsibleContent = document.createElement("div");
        this.collapsibleContent.classList.add("collapsible-content");
        this.container.appendChild(this.collapsibleContent);
        if (options.collapsible) {
            container.classList.add("control-collapsed");
            container.classList.add("collapsible");
            this.boundToggleCollapsedState = this.toggleCollapseState.bind(this);
            button.addEventListener("click", this.boundToggleCollapsedState);
            let collapsedSymbol = options.collapsedSymbol;
            if (typeof(collapsedSymbol) === "undefined") {
                collapsedSymbol = document.createElementNS("http://www.w3.org/2000/svg", "svg"); // Bootstrap ikona Chevron up https://icons.getbootstrap.com/icons/chevron-up/
                collapsedSymbol.innerHTML = `<path fill-rule="evenodd" d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z"/>`;
                collapsedSymbol.setAttribute("width", "24px");
                collapsedSymbol.setAttribute("height", "24px");
                collapsedSymbol.setAttribute("viewBox", "0 0 16 16");
            }
            collapsedSymbol.classList.add("collapsed-icon");
            button.appendChild(collapsedSymbol);
        }
        if (options.tooltipText) {
            let res = this.createTooltip(options.tooltipText, options.tooltipContainer ?? this.container, button, false, options.tooltipLocation);
            this.tooltipTextDiv = res.tooltipTextDiv;
            this.popper = res.popper;
        }
    }

    protected generateCheckBox(id: string, label: string, checked:boolean, changeHandler: () => void, container: HTMLElement) {
        let input = document.createElement("input");
        input.type = "checkbox";
        input.id = id;
        input.value = label;
        input.checked = checked;
        input.addEventListener("change", changeHandler);
        let inputLabel = document.createElement("label");
        inputLabel.textContent = label;
        inputLabel.htmlFor = id;
        container.appendChild(input);
        container.appendChild(inputLabel);
    }

    protected createTooltip(tooltipText: string, tooltipContainer:HTMLDivElement, tooltipRoot:HTMLElement, showOnFocus:boolean = false, placement: Placement = "right"): ITooltipResult {
        let tooltipDiv = document.createElement("div");
        tooltipDiv.classList.add("control-tooltip");
        let tooltipArrow = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        tooltipArrow.innerHTML = `<path d="M 10,0 0,5 10,10" />`;
        tooltipArrow.setAttribute("width", "10px");
        tooltipArrow.setAttribute("height", "10px");
        tooltipArrow.setAttribute("viewBox", "0 0 10 10");
        tooltipArrow.classList.add("control-tooltip-arrow");
        let tooltipTextDiv = document.createElement("div");
        tooltipTextDiv.innerText = tooltipText;
        tooltipTextDiv.classList.add("control-tooltip-text");
        let arrowContainer = document.createElement("div");
        arrowContainer.setAttribute("data-popper-arrow", "true");
        arrowContainer.append(tooltipArrow);
        tooltipDiv.append(arrowContainer, tooltipTextDiv);
        let popper = createPopper(tooltipRoot, tooltipDiv, {
            placement: placement,
            modifiers: [
                {
                    name: "arrow",
                    options: {
                        element: arrowContainer
                    }
                }
            ]
        });
        tooltipRoot.addEventListener("pointerenter", () => showTooltip(tooltipDiv, popper));
        tooltipRoot.addEventListener("pointerleave", () => hideTooltip(tooltipDiv, popper));
        if (!showOnFocus)
            tooltipRoot.addEventListener("focus", () => hideTooltip(tooltipDiv, popper));
        tooltipContainer.appendChild(tooltipDiv);
        return {
            popper: popper,
            tooltipTextDiv: tooltipTextDiv
        }
    }

    private toggleCollapseState() {
        if (this.container.classList.contains("control-collapsed")) {
            this.container.classList.remove("control-collapsed");
        } else {
            this.container.classList.add("control-collapsed");
        }
    }
}

function showTooltip(tooltipDiv: HTMLDivElement, popper: Instance) {
    if (tooltipDiv && popper) {
        tooltipDiv.setAttribute("data-show", "");
        popper.update();
    }
}

function hideTooltip(tooltipDiv: HTMLDivElement, popper: Instance) {
    if (tooltipDiv && popper) {
        tooltipDiv.removeAttribute("data-show");
        popper.update();
    }
}